import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import { changePassword, updateMe } from "../../services/accountService";
import { getObservations } from "../../services/observationService";
import { computeOverallScoreFromApi } from "../../utils/helpers/scoring";
import { FINALIZED_STATUSES } from "../../utils/constants/observations";
import { fullName } from "../../utils/helpers/userDisplay";
import Avatar from "../../components/Avatar/Avatar";
import { formatShortDate } from "../../utils/helpers/dateDisplay";
import { ROLE_CODES, roleNamesLabel } from "../../utils/permissions";
import DonutProgress from "../../components/DonutProgress/DonutProgress";
import Spinner from "../../components/Spinner/Spinner";
import { formatApiError } from "../../utils/helpers/apiErrors";

const FINALIZED_STATUS_PARAM = [...FINALIZED_STATUSES].join(",");

const labelClass = "block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5";
const inputClass =
  "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition";
const cardClass = "bg-white rounded-[24px] border border-slate-200 shadow-sm p-6";
const saveBtnClass =
  "px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition";
const readOnlyClass =
  "rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-2.5 text-sm text-slate-700";

// The account fields a directory can own. `managed_fields` also names staff-link
// fields (organization, start_date, …) this page renders read-only regardless.
const ACCOUNT_FIELDS = ["first_name", "last_name", "email"];

// One account field: an input, or — when the directory owns it — the same
// read-only box the school-assigned teaching fields already use. Offering an
// input for a field bilorg owns would only earn a 400 on save.
function AccountField({ label, value, managed, onChange, type = "text", wrapperClass }) {
  return (
    <div className={wrapperClass}>
      <label className={labelClass}>{label}</label>
      {managed ? (
        <p className={readOnlyClass}>{value || "—"}</p>
      ) : (
        <input type={type} className={inputClass} value={value} onChange={onChange} />
      )}
    </div>
  );
}

export default function Profile() {
  const { t } = useTranslation();
  const { user, setUser } = useContext(UserContext);
  // Detect teachers by role, not by an existing teacher_profile row — newly
  // created teacher accounts have no profile yet (the backend creates one on
  // first save), but they still need the subject/gender/portrait fields.
  const isTeacher = (user?.roles || []).some((r) => r.code === ROLE_CODES.TEACHER);
  // Which of my own fields the directory maintains, straight off `/auth/me/`.
  // `managed_by` is null for a locally created account, which leaves this form
  // exactly as it was.
  const directory = user?.managed_by || null;
  const managedFields = user?.managed_fields || [];
  const editableAccountFields = ACCOUNT_FIELDS.filter(
    (field) => !directory || !managedFields.includes(field),
  );

  const [account, setAccount] = useState({ first_name: "", last_name: "", email: "" });
  // Department + teaching subjects are manager-assigned (read-only here); only
  // gender stays self-editable.
  const [gender, setGender] = useState("");
  const [pwd, setPwd] = useState({ old_password: "", new_password: "", confirm: "" });

  const [savingAccount, setSavingAccount] = useState(false);
  const [savingTeaching, setSavingTeaching] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);

  // undefined = still loading (pulse placeholder); null = loaded but no
  // counted observations (DonutProgress renders a slate "—").
  const [score, setScore] = useState(undefined);
  const fileInputRef = useRef(null);

  // Sync local forms whenever the authenticated user loads / changes.
  useEffect(() => {
    if (!user) return;
    setAccount({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
    });
    setGender(user.teacher_profile?.gender || "");
  }, [user]);

  // Performance snapshot for teachers — same rule as TeacherDashboard.
  useEffect(() => {
    if (!isTeacher || !user?.uuid) return;
    const controller = new AbortController();
    getObservations({
      params: {
        observed_teacher__uuid: user.uuid,
        status__in: FINALIZED_STATUS_PARAM,
        ordering: "-observed_at",
      },
      signal: controller.signal,
    })
      .then((res) => {
        const list = res?.results || res || [];
        const raw = computeOverallScoreFromApi(list);
        // null = no counted observations — the hero hides the donut instead
        // of showing a red 0%.
        setScore(raw == null ? null : Math.round(raw));
      })
      .catch((err) => {
        if (err?.code !== "ERR_CANCELED") setScore(null);
      });
    return () => controller.abort();
  }, [isTeacher, user?.uuid]);

  // Trivial string comparisons that already re-run whenever account/teaching
  // change (on every keystroke) — plain consts, no useMemo needed.
  const accountDirty =
    account.first_name !== (user?.first_name || "") ||
    account.last_name !== (user?.last_name || "") ||
    account.email !== (user?.email || "");

  const teachingDirty = gender !== (user?.teacher_profile?.gender || "");

  if (!user) {
    return (
      <div className="p-6 bg-slate-50 min-h-full flex items-center justify-center">
        <Spinner label={t("profile.loading")} size="md" />
      </div>
    );
  }

  // Department + teaching subjects from the primary staff link. The backend
  // already orders `organizations` by -is_in_progress, -start_date, so the
  // active link is first.
  const primaryStaff = (user.organizations || [])[0] || null;
  const staffDepartment = primaryStaff?.department?.name || "";
  const staffSubjects = primaryStaff?.subjects || [];
  const roleLabel =
    roleNamesLabel(user) ||
    (isTeacher ? t("profile.roleTeacher") : t("profile.roleStaff"));

  // Shared save path: PATCH /auth/me/, refresh UserContext, toast, clear loading.
  const saveChanges = (body, setLoading, successMsg, errMsg) => {
    setLoading(true);
    updateMe({ body })
      .then((updated) => {
        setUser(updated);
        toast.success(successMsg);
      })
      .catch((err) => toast.error(formatApiError(err, errMsg)))
      .finally(() => setLoading(false));
  };

  // Only the fields this side still owns: a directory-owned one comes back a
  // 400 naming bilorg, and its input is read-only above anyway.
  const saveAccount = () =>
    saveChanges(
      Object.fromEntries(editableAccountFields.map((f) => [f, account[f]])),
      setSavingAccount,
      t("profile.updated"),
      t("profile.updateError"),
    );

  const saveTeaching = () =>
    saveChanges({ gender }, setSavingTeaching, t("profile.teachingUpdated"), t("profile.teachingError"));

  const onPortraitPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("profile.chooseImage"));
      return;
    }
    saveChanges({ portrait_image: file }, setUploadingPortrait, t("profile.photoUpdated"), t("profile.photoError"));
  };

  const submitPassword = (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 8) {
      toast.error(t("profile.passwordMinLength"));
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error(t("profile.passwordsMismatch"));
      return;
    }
    setSavingPwd(true);
    changePassword({ body: { old_password: pwd.old_password, new_password: pwd.new_password } })
      .then(() => {
        toast.success(t("profile.passwordChanged"));
        setPwd({ old_password: "", new_password: "", confirm: "" });
      })
      .catch((err) => toast.error(formatApiError(err, t("profile.passwordError"))))
      .finally(() => setSavingPwd(false));
  };

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-[32px] bg-slate-900 p-8 text-white shadow-xl">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-indigo-600/20 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="relative shrink-0">
              <Avatar
                user={user}
                alt={fullName(user)}
                className="w-24 h-24 rounded-full bg-white shadow-xl ring-4 ring-white/10 text-indigo-600 text-2xl font-black"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPortrait}
                title={t("profile.changePhoto")}
                className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-indigo-600 border-2 border-slate-900 flex items-center justify-center hover:bg-indigo-500 transition disabled:opacity-60"
              >
                {uploadingPortrait ? (
                  <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPortraitPick}
              />
            </div>

            {/* Identity */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-3xl font-black tracking-tight leading-none">{fullName(user)}</h1>
              <p className="mt-1 text-sm text-slate-400">{user.email}</p>
              <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <span className="px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border border-white/20 bg-white/10 text-indigo-200">
                  {roleLabel}
                </span>
                {staffDepartment && (
                  <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border border-white/10 bg-white/5 text-slate-300">
                    {staffDepartment}
                  </span>
                )}
              </div>
            </div>

            {/* Score */}
            {isTeacher && (
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                {score === undefined ? (
                  <div className="w-[72px] h-[72px] rounded-full bg-white/5 animate-pulse" />
                ) : (
                  <div className="bg-white rounded-2xl p-1.5">
                    <DonutProgress score={score} size={72} strokeWidth={6} labelSize="text-sm" />
                  </div>
                )}
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {t("profile.performance")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Account information */}
        <div className={cardClass}>
          <h2 className="text-lg font-black text-slate-900 mb-5">{t("profile.accountInfo")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AccountField
              label={t("profile.firstName")}
              value={account.first_name}
              managed={!editableAccountFields.includes("first_name")}
              onChange={(e) => setAccount((a) => ({ ...a, first_name: e.target.value }))}
            />
            <AccountField
              label={t("profile.lastName")}
              value={account.last_name}
              managed={!editableAccountFields.includes("last_name")}
              onChange={(e) => setAccount((a) => ({ ...a, last_name: e.target.value }))}
            />
            <AccountField
              label={t("profile.email")}
              type="email"
              wrapperClass="sm:col-span-2"
              value={account.email}
              managed={!editableAccountFields.includes("email")}
              onChange={(e) => setAccount((a) => ({ ...a, email: e.target.value }))}
            />
          </div>
          {/* Only when the directory actually owns a field on THIS card — it
              also owns staff-link fields the card never renders. */}
          {directory && editableAccountFields.length < ACCOUNT_FIELDS.length && (
            <p className="mt-3 text-xs text-slate-400">
              {t("profile.managedFields", { name: directory.name })}
            </p>
          )}
          {/* No save button when bilorg owns every field on this card — a
              button whose only possible outcome is a 400 is worse than none. */}
          {editableAccountFields.length > 0 && (
            <div className="mt-5 flex justify-end">
              <button className={saveBtnClass} onClick={saveAccount} disabled={!accountDirty || savingAccount}>
                {savingAccount ? t("common.saving") : t("common.saveChanges")}
              </button>
            </div>
          )}
        </div>

        {/* Teaching details (teachers only) */}
        {isTeacher && (
          <div className={cardClass}>
            <h2 className="text-lg font-black text-slate-900 mb-5">{t("profile.teachingDetails")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Department + subjects are assigned by the school (read-only). */}
              <div>
                <label className={labelClass}>{t("profile.department")}</label>
                <p className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-2.5 text-sm text-slate-700">
                  {staffDepartment || t("profile.noneAssigned")}
                </p>
              </div>
              <div>
                <label className={labelClass}>{t("profile.gender")}</label>
                <select
                  className={inputClass}
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="">{t("profile.genderUnspecified")}</option>
                  <option value="male">{t("profile.genderMale")}</option>
                  <option value="female">{t("profile.genderFemale")}</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>{t("profile.teachingSubjects")}</label>
                {staffSubjects.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {staffSubjects.map((s) => (
                      <span
                        key={s.uuid}
                        className="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700"
                      >
                        {s.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">{t("profile.noneAssigned")}</p>
                )}
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-400">{t("profile.assignedBySchool")}</p>
            <div className="mt-5 flex justify-end">
              <button className={saveBtnClass} onClick={saveTeaching} disabled={!teachingDirty || savingTeaching}>
                {savingTeaching ? t("common.saving") : t("common.saveChanges")}
              </button>
            </div>
          </div>
        )}

        {/* Affiliation (read-only) */}
        {user.organizations?.length > 0 && (
          <div className={cardClass}>
            <h2 className="text-lg font-black text-slate-900 mb-5">{t("profile.affiliation")}</h2>
            <div className="space-y-3">
              {user.organizations.map((org) => {
                const meta = [
                  org.department?.name,
                  (org.subjects || []).map((s) => s.name).join(", "),
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                <div
                  key={org.uuid}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">
                      {org.organization?.name || "—"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {meta && `${meta} · `}
                      {formatShortDate(org.start_date)} – {org.is_in_progress ? t("profile.present") : formatShortDate(org.end_date)}
                    </p>
                  </div>
                  {org.is_in_progress && (
                    <span className="px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full bg-emerald-100 text-emerald-700">
                      {t("profile.active")}
                    </span>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Security — a directory account has no local password (it signs in
            via the directory's own SSO), so the change-password form here can
            only ever end in the server's "wrong password" check. */}
        <div className={cardClass}>
          <h2 className="text-lg font-black text-slate-900 mb-5">{t("profile.security")}</h2>
          {directory ? (
            <p className="text-sm text-slate-500">
              {t("profile.securityManaged", { name: directory.name })}
            </p>
          ) : (
            <form onSubmit={submitPassword} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>{t("profile.currentPassword")}</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  className={inputClass}
                  value={pwd.old_password}
                  onChange={(e) => setPwd((p) => ({ ...p, old_password: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelClass}>{t("profile.newPassword")}</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  className={inputClass}
                  value={pwd.new_password}
                  onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelClass}>{t("profile.confirmPassword")}</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  className={inputClass}
                  value={pwd.confirm}
                  onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-3 flex justify-end">
                <button
                  type="submit"
                  className={saveBtnClass}
                  disabled={savingPwd || !pwd.old_password || !pwd.new_password || !pwd.confirm}
                >
                  {savingPwd ? t("profile.updating") : t("profile.updatePassword")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
